import { useState, useEffect } from 'react';
import usePutRequest from '../../Hooks/usePutRequest';
import {
    TextField, Button, MenuItem, Typography, Box, InputLabel,
    Select, FormControl, OutlinedInput, Chip, Stack, Avatar,
    Paper, Alert, CircularProgress
} from '@mui/material';
import {
    PhotoCamera, Person, LocationOn,
    Category, Link,
} from '@mui/icons-material';
import { useAuth } from '../../Context/AuthContext';
import useDeleteRequest from '../../Hooks/useDeleteRequest';
import { brandGradient } from '../../shared-theme/themePrimitives';
const Gender = ['Male', 'Female'];
const industries = ['Fashion', 'Tech', 'Food', 'Education'];
const content_category = ['Music', 'Fashion', 'Gaming', 'Healthcare', 'Education', 'Travel', 'Comedy', 'Tech'];
const platforms = ['Instagram', 'YouTube', 'TikTok', 'Twitter/X', 'Facebook', 'Reddit'];


export default function CreatorProfileForm({ onSave, defaultValues, isEditing = false }) {
    const { user } = useAuth();
    const { put, loading } = usePutRequest();
    const mapProfileToFormData = (profile) => {
        if (!profile) return {};

        return {
            name: user?.name || '',
            email: user?.email || '',
            profile_image: profile?.creator?.profile_image || '',
            gender: profile?.creator?.gender || '',
            bio: profile?.creator?.bio || '',
            content_category: profile?.creator?.content_category || [],
            description: profile?.creator?.description || '',
            street: profile?.address?.street || '',
            city: profile?.address?.city || '',
            state: profile?.address?.state || '',
            zip: profile?.address?.zip || '',
            country: profile?.address?.country || '',
            platforms: profile?.platforms || [],
        };
    };
    const { del } = useDeleteRequest();


    const [formData, setFormData] = useState(() => {
        if (isEditing && defaultValues) {
            return mapProfileToFormData(defaultValues);
        }
        return {
            name: user?.name || '',
            email: user?.email || '',
            profile_image: '',
            gender: '',
            bio: '',
            content_category: [],
            description: '',
            street: '',
            city: '',
            state: '',
            zip: '',
            country: '',
            platforms: [],
        };
    });

    useEffect(() => {
        if (isEditing && defaultValues) {
            setFormData(mapProfileToFormData(defaultValues));
        }
    }, [defaultValues, isEditing]);

    const [submitError, setSubmitError] = useState('');

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleImageUpload = (e, field) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.size > 5 * 1024 * 1024) {
            setSubmitError('Image size should be less than 5MB');
            return;
        }

        const reader = new FileReader();
        reader.onloadend = () => {
            handleChange(field, reader.result);
        };
        reader.readAsDataURL(file);
    };

    const handlePlatformChange = (index, field, value) => {
        const updatedPlatforms = [...formData.platforms];
        if (!updatedPlatforms[index]) {
            updatedPlatforms[index] = {
                name: '',
                profile_url: '',
                profile_id: '',
                genre: [],
                reel_price: 0,
                story_price: 0,
                live_price: 0
            };
        }
        updatedPlatforms[index][field] = value;

        if (field === 'name' && value === 'YouTube' && !updatedPlatforms[index].profile_id) {
            updatedPlatforms[index].profile_id = '';
        }

        handleChange('platforms', updatedPlatforms);
    };

    const addPlatform = () => {
        const newPlatform = {
            name: '',
            profile_url: '',
            profile_id: '',
            genre: [],
            reel_price: 0,
            story_price: 0,
            live_price: 0
        };
        handleChange('platforms', [...formData.platforms, newPlatform]);
    };

    const DeletePlatform = async (id) => {
        try {
            const response = await del(`delete-platform/${id}`);
            return response;
        } catch (error) {
            console.error("Update failed", error);
        }

    }
    const removePlatform = (index) => {
        const updatedPlatforms = formData.platforms.filter((_, i) => i !== index);
        const removedPlatform = formData.platforms.filter((_, i) => i === index);
        DeletePlatform(removedPlatform[0].id);

        handleChange('platforms', updatedPlatforms);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitError('');

        if (!formData.bio.trim()) {
            setSubmitError('Bio is required');
            return;
        }

        if (formData.content_category.length === 0) {
            setSubmitError('Please select at least one content category');
            return;
        }

        for (const platform of formData.platforms) {
            if (platform.name === 'YouTube' && !platform.profile_id?.trim()) {
                setSubmitError('Profile ID is required');
                return;
            }
        }

        try {
            const response = await put('/update-profile', formData);

            if (response === 200) {
                await onSave();
            } else {
                setSubmitError("Failed to update profile. Please try again.");
            }
        } catch (error) {
            console.error("Update failed", error);
            setSubmitError(error.message || "An error occurred while updating the profile.");
        }
    };

    return (
        <Box sx={{ maxWidth: 800, mx: 'auto', my: 4, px: 2 }}>
            <Paper elevation={0} sx={{ p: { xs: 2.5, sm: 4 }, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="h4" sx={{
                    fontWeight: 700,
                    background: brandGradient,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    display: 'inline-block',
                    textAlign: "center", mb: 3, width: '100%'
                }}>
                    {isEditing ? 'Edit Creator Profile' : 'Create Creator Profile'}
                </Typography>

                {submitError && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {submitError}
                    </Alert>
                )}

                <form onSubmit={handleSubmit}>
                    <Paper elevation={0} sx={{ p: 3, borderRadius: 2, mb: 3, border: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="h6" sx={{ color: 'secondary.main', mb: 2 }}>
                            <Person sx={{ mr: 1 }} /> Personal Information
                        </Typography>
                        <Stack spacing={2}>
                            <TextField
                                label="Bio"
                                fullWidth
                                required
                                multiline
                                rows={1}
                                value={formData.bio}
                                onChange={(e) => handleChange('bio', e.target.value)}
                                placeholder="Tell us about yourself and your content style..."
                            />
                            <FormControl fullWidth>
                                <InputLabel>Gender</InputLabel>
                                <Select
                                    value={formData.gender}
                                    onChange={(e) => handleChange('gender', e.target.value)}
                                    label="Gender"
                                >
                                    {Gender.map((gender) => (
                                        <MenuItem key={gender} value={gender}>{gender}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <TextField
                                label="Description"
                                fullWidth
                                multiline
                                rows={1}
                                value={formData.description}
                                onChange={(e) => handleChange('description', e.target.value)}
                                placeholder="Additional description about your content or services..."
                            />
                        </Stack>
                    </Paper>

                    <Paper elevation={0} sx={{ p: 3, borderRadius: 2, mb: 3, border: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="h6" sx={{ color: 'secondary.main', mb: 2 }}>
                            <Category sx={{ mr: 1 }} /> Content Categories
                        </Typography>
                        <FormControl fullWidth>
                            <InputLabel>Content Categories</InputLabel>
                            <Select
                                multiple
                                value={formData.content_category}
                                onChange={(e) => handleChange('content_category', e.target.value)}
                                input={<OutlinedInput label="Content Categories" />}
                                renderValue={(selected) => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {selected.map(value => (
                                            <Chip key={value} label={value} />
                                        ))}
                                    </Box>
                                )}
                            >
                                {content_category.map(cat => (
                                    <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Paper>

                    <Paper elevation={0} sx={{ p: 3, borderRadius: 2, mb: 3, border: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="h6" sx={{ color: 'secondary.main', mb: 2 }}>
                            <LocationOn sx={{ mr: 1 }} /> Location Information
                        </Typography>
                        <Stack spacing={2}>
                            <TextField
                                label="Street Address"
                                fullWidth
                                value={formData.street}
                                onChange={(e) => handleChange('street', e.target.value)}
                            />
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                <TextField
                                    label="City"
                                    fullWidth
                                    value={formData.city}
                                    onChange={(e) => handleChange('city', e.target.value)}
                                />
                                <TextField
                                    label="State"
                                    fullWidth
                                    value={formData.state}
                                    onChange={(e) => handleChange('state', e.target.value)}
                                />
                            </Stack>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                <TextField
                                    label="ZIP Code"
                                    fullWidth
                                    value={formData.zip}
                                    onChange={(e) => handleChange('zip', e.target.value)}
                                />
                                <TextField
                                    label="Country"
                                    fullWidth
                                    value={formData.country}
                                    onChange={(e) => handleChange('country', e.target.value)}
                                />
                            </Stack>
                        </Stack>
                    </Paper>

                    <Paper elevation={0} sx={{ p: 3, borderRadius: 2, mb: 3, border: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="h6" sx={{ color: 'secondary.main', mb: 2 }}>
                            <Link sx={{ mr: 1 }} /> Social Media Platforms
                        </Typography>

                        {formData.platforms.map((platform, index) => (
                            <Paper key={index} elevation={0} sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                                <Stack spacing={2}>
                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
                                        <FormControl sx={{ minWidth: 150 }}>
                                            <InputLabel>Platform</InputLabel>
                                            <Select
                                                value={platform.name || ''}
                                                onChange={(e) => handlePlatformChange(index, 'name', e.target.value)}
                                                label="Platform"
                                            >
                                                {platforms.map(p => (
                                                    <MenuItem key={p} value={p}>{p}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <TextField
                                            label="Profile URL"
                                            fullWidth
                                            value={platform.profile_url || ''}
                                            onChange={(e) => handlePlatformChange(index, 'profile_url', e.target.value)}
                                            placeholder="https://..."
                                        />
                                        <Button
                                            variant="outlined"
                                            color="error"
                                            onClick={() => removePlatform(index)}
                                            sx={{ minWidth: 'auto', px: 2 }}
                                        >
                                            Remove
                                        </Button>
                                    </Stack>

                                    {/* Show profile_id field only for YouTube */}
                                    {/* {platform.name === 'YouTube' && ( */}
                                    <TextField
                                        label="Channel ID"
                                        fullWidth
                                        value={platform.profile_id || ''}
                                        onChange={(e) => handlePlatformChange(index, 'profile_id', e.target.value)}
                                        placeholder="Enter your channel ID"
                                        helperText="This is typically found in your channel URL"
                                    />
                                    {/* )} */}

                                    <FormControl fullWidth>
                                        <InputLabel>Genre/Niche</InputLabel>
                                        <Select
                                            multiple
                                            value={platform.genre || []}
                                            onChange={(e) => handlePlatformChange(index, 'genre', e.target.value)}
                                            input={<OutlinedInput label="Genre/Niche" />}
                                            renderValue={(selected) => (
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                    {selected.map(value => (
                                                        <Chip key={value} label={value} size="small" />
                                                    ))}
                                                </Box>
                                            )}
                                        >
                                            {industries.map(industry => (
                                                <MenuItem key={industry} value={industry}>{industry}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                        <TextField
                                            label="Reel Price ($)"
                                            type="number"
                                            fullWidth
                                            value={platform.reel_price || 0}
                                            onChange={(e) => handlePlatformChange(index, 'reel_price', Number(e.target.value))}
                                            InputProps={{ inputProps: { min: 0 } }}
                                        />
                                        <TextField
                                            label="Story Price ($)"
                                            type="number"
                                            fullWidth
                                            value={platform.story_price || 0}
                                            onChange={(e) => handlePlatformChange(index, 'story_price', Number(e.target.value))}
                                            InputProps={{ inputProps: { min: 0 } }}
                                        />
                                        <TextField
                                            label="Live Price ($)"
                                            type="number"
                                            fullWidth
                                            value={platform.live_price || 0}
                                            onChange={(e) => handlePlatformChange(index, 'live_price', Number(e.target.value))}
                                            InputProps={{ inputProps: { min: 0 } }}
                                        />
                                    </Stack>
                                </Stack>
                            </Paper>
                        ))}

                        <Button
                            variant="outlined"
                            onClick={addPlatform}
                            sx={{ mt: 2 }}
                            fullWidth
                        >
                            Add Platform
                        </Button>
                    </Paper>

                    <Paper elevation={0} sx={{ p: 3, borderRadius: 2, mb: 3, border: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="h6" sx={{ color: 'secondary.main', mb: 2 }}>
                            <PhotoCamera sx={{ mr: 1 }} /> Profile Image
                        </Typography>

                        <Box sx={{ textAlign: 'center' }}>
                            <Button variant="outlined" component="label" sx={{ mb: 2 }}>
                                Upload Profile Image
                                <input
                                    hidden
                                    accept="image/*"
                                    type="file"
                                    onChange={(e) => handleImageUpload(e, 'profile_image')}
                                />
                            </Button>
                            {formData.profile_image && (
                                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                    <Avatar
                                        src={formData.profile_image.startsWith('data:')
                                            ? formData.profile_image
                                            : `${process.env.REACT_APP_BASE_URL}${formData.profile_image}`
                                        }
                                        sx={{ width: 120, height: 120 }}
                                    />
                                </Box>
                            )}
                        </Box>
                    </Paper>

                    <Box sx={{ textAlign: 'right' }}>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={loading}
                            sx={{
                                backgroundImage: brandGradient,
                                color: '#fff',
                                borderRadius: 50,
                                px: 4,
                                minWidth: 120,
                            }}
                        >
                            {loading ? (
                                <CircularProgress size={24} color="inherit" />
                            ) : (
                                isEditing ? 'Update Profile' : 'Save Profile'
                            )}
                        </Button>
                    </Box>
                </form>
            </Paper>
        </Box>
    );
}